import { getFileIconType } from "src/components/common/FileIcon/FileIconType";

const MIME_EXTENSIONS: Record<string, string> = {
  "application/pdf": "pdf",
  "application/rtf": "rtf",
  "application/x-tar": "tar",
  "application/x-bzip2": "bz2",
  "application/x-xz": "xz",
  "application/x-rar-compressed": "rar",
  "audio/x-wav": "wav",
  "audio/x-flac": "flac",
  "video/x-msvideo": "avi",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "pptx",
  "application/vnd.oasis.opendocument.text": "odt",
  "application/vnd.oasis.opendocument.spreadsheet": "ods",
  "application/vnd.oasis.opendocument.presentation": "odp",
  "application/epub+zip": "epub",
  "application/zip": "zip",
  "application/gzip": "gz",
  "application/x-7z-compressed": "7z",
  "application/vnd.rar": "rar",
  "application/json": "json",
  "application/xml": "xml",
  "application/yaml": "yaml",
  "text/plain": "txt",
  "text/html": "html",
  "text/css": "css",
  "text/csv": "csv",
  "text/markdown": "md",
  "text/xml": "xml",
  "text/rtf": "rtf",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/avif": "avif",
  "image/svg+xml": "svg",
  "image/heic": "heic",
  "image/tiff": "tiff",
  "audio/mpeg": "mp3",
  "audio/mp4": "m4a",
  "audio/wav": "wav",
  "audio/ogg": "ogg",
  "audio/flac": "flac",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
  "video/x-matroska": "mkv",
};

export interface FileFormatSource {
  filename?: string | null;
  extension?: string | null;
  mimeType?: string | null;
}

/** Keep filename-only formats available without inventing a file extension. */
export function getFileIconInfo(source: FileFormatSource) {
  const filenameInfo = getFileIconType(source.filename ?? "");
  const mime = source.mimeType?.split(";")[0]?.trim().toLowerCase();
  if (
    !source.extension?.trim() &&
    filenameInfo.family !== "unknown" &&
    !filenameInfo.extension &&
    (!mime || mime === "text/plain" || mime === "application/octet-stream")
  ) {
    return filenameInfo;
  }
  const extension = getFileExtension(source);
  return getFileIconType(extension ? `file.${extension}` : (source.filename ?? ""));
}

/** Stored extensions win over editable names; MIME types fill in missing extensions. */
export function getFileExtension({
  filename,
  extension,
  mimeType,
}: FileFormatSource): string | undefined {
  const explicit = extension?.trim().replace(/^\./, "").toLowerCase();
  if (explicit && /^[a-z0-9][a-z0-9.+-]*$/.test(explicit)) return explicit;
  const mime = mimeType?.split(";")[0]?.trim().toLowerCase();
  const known = filename ? getFileIconType(filename).extension : undefined;
  const subtype = mime?.split("/")[1];
  const registeredSubtype =
    subtype && getFileIconType(`file.${subtype}`).extension === subtype ? subtype : undefined;
  const inferred = mime ? (MIME_EXTENSIONS[mime] ?? registeredSubtype) : undefined;
  if (inferred) return known?.endsWith(`.${inferred}`) ? known : inferred;
  if (!filename) return undefined;
  if (known) return known;
  const suffix = filename
    .split(/[\\/]/)
    .pop()
    ?.match(/\.([a-z0-9][a-z0-9+-]*)$/i)?.[1];
  return suffix?.toLowerCase();
}

/** MIME types by extension, from the table above (later entries are the preferred names). */
const EXTENSION_MIME_TYPES: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(MIME_EXTENSIONS).map(([mime, extension]) => [extension, mime]),
  ),
  jpeg: "image/jpeg",
  tif: "image/tiff",
};

/**
 * The MIME type to upload a file as. Browsers leave `File.type` empty for some files (Firefox does
 * for files picked as part of a directory), so the extension fills in; the server refuses
 * `application/octet-stream`, which is left only for files with no known extension.
 */
export function getUploadMimeType(file: { name: string; type: string }): string {
  if (file.type) return file.type;
  const extension = file.name.match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase();
  return (extension && EXTENSION_MIME_TYPES[extension]) || "application/octet-stream";
}
